/**
 * クイック投稿フォームの定義。
 *
 * 「かんたん更新」のうち、ページ編集画面を開かずに 1 件追加できる
 * ブロック型（お知らせ・解決事例・メニュー品目・商品など）について、
 * 素人向けの最小フォームを宣言的に定義する。
 *
 * ここに定義があるブロック型は、CMS 管理画面・admin テナントダッシュボードの
 * かんたん更新カードから `/admin/quick-update?page={id}&block={blockType}` の
 * 専用フォームに誘導される。フォームの描画と保存（`POST /api/quick-update`）は
 * apps/cms 側の責務で、ここは Payload に依存しない純粋ロジックのみを置く。
 *
 * 2 つの配列形状に対応する:
 * - フラット型: block[itemsField] に直接追加（news / lawNews / cases / product_grid）
 * - カテゴリ入れ子型: block[grouping.field][i][grouping.itemsField] に追加
 *   （menu_list / service_menu）。カテゴリ自体の作成は詳細編集に委譲する。
 */

export type QuickFormFieldOption = {
  label: string;
  value: string;
};

export type QuickFormField = {
  name: string;
  label: string;
  type: 'checkbox' | 'date' | 'image' | 'number' | 'select' | 'text' | 'textarea' | 'url';
  required?: boolean;
  placeholder?: string;
  /** type: 'select' の場合の選択肢 */
  options?: readonly QuickFormFieldOption[];
  defaultValue?: string;
  /** type: 'date' の場合、フォーム初期値を今日にする */
  defaultToToday?: boolean;
  /** type: 'number' の場合の制約 */
  min?: number;
  max?: number;
  integer?: boolean;
  /** 指定時、ブロックの layout がこのいずれかのときだけフォームに表示する */
  layouts?: readonly string[];
  hint?: string;
};

export type QuickFormGrouping = {
  /** ブロック内のカテゴリ配列フィールド名（例: categories） */
  field: string;
  /** カテゴリ名を持つフィールド名（例: name） */
  nameField: string;
  /** カテゴリ内の品目配列フィールド名（例: items） */
  itemsField: string;
  /** UI 上の呼び名（例: カテゴリ） */
  label: string;
};

export type QuickFormDescriptor = {
  blockType: string;
  /** フラット型ブロックの配列フィールド名（grouping がある場合は未使用） */
  itemsField: string;
  /** カテゴリ入れ子型ブロックの定義 */
  grouping?: QuickFormGrouping;
  /** フォーム見出し（例: お知らせを投稿） */
  title: string;
  /** 追加する 1 件の呼び名（例: お知らせ） */
  itemLabel: string;
  description: string;
  /** 配列の上限。grouping がある場合はカテゴリ内の上限 */
  maxRows?: number;
  /**
   * 上限到達時の挙動。
   * rotate: 反対端の最も古い 1 件を削る（お知らせ向け）
   * error: 追加を拒否して詳細編集へ誘導（メニュー・商品向け — 勝手に消さない）
   */
  whenFull: 'error' | 'rotate';
  /** 追加位置。お知らせは先頭（新しい順）、メニューは末尾（並び順を保つ） */
  insertPosition: 'end' | 'start';
  /** 既存掲載の見出しとして表示するフィールド名（title / name） */
  titleField: string;
  /**
   * 複数枚一括追加（写真ギャラリー向け）。imageField だけがファイルごとに
   * 変わり、他のフィールドは全件に共通適用される。disabledForLayouts に
   * 該当するレイアウト（例: pairs は before/after の組で 1 件ずつ）では
   * 一括モードを使わない。
   */
  batch?: {
    imageField: string;
    maxPerRequest: number;
    disabledForLayouts?: readonly string[];
  };
  fields: readonly QuickFormField[];
};

const NEWS_FORM: QuickFormDescriptor = {
  blockType: 'news',
  itemsField: 'items',
  title: 'お知らせを投稿',
  itemLabel: 'お知らせ',
  description: '休診情報やお知らせを 1 件追加します。入力して保存するだけで掲載準備が整います。',
  maxRows: 20,
  whenFull: 'rotate',
  insertPosition: 'start',
  titleField: 'title',
  fields: [
    { name: 'published_at', label: '日付', type: 'date', required: true, defaultToToday: true },
    {
      name: 'category',
      label: '種類',
      type: 'select',
      defaultValue: 'notice',
      options: [
        { label: 'お知らせ', value: 'notice' },
        { label: '休診情報', value: 'closure' },
        { label: '院内告知', value: 'announcement' },
      ],
    },
    {
      name: 'title',
      label: 'タイトル',
      type: 'text',
      required: true,
      placeholder: '例: 8月13日〜15日は休診します',
    },
    {
      name: 'body',
      label: '本文',
      type: 'textarea',
      placeholder: '詳しい内容があれば入力してください（省略可）',
    },
  ],
};

const LAW_NEWS_FORM: QuickFormDescriptor = {
  blockType: 'lawNews',
  itemsField: 'items',
  title: 'お知らせ・コラムを投稿',
  itemLabel: '記事',
  description: '事務所からのお知らせやコラムを 1 件追加します。',
  maxRows: 20,
  whenFull: 'rotate',
  insertPosition: 'start',
  titleField: 'title',
  fields: [
    { name: 'publishedAt', label: '日付', type: 'date', required: true, defaultToToday: true },
    {
      name: 'category',
      label: '種類',
      type: 'select',
      defaultValue: 'notice',
      options: [
        { label: 'コラム', value: 'column' },
        { label: '法改正情報', value: 'legal_update' },
        { label: 'お知らせ', value: 'notice' },
      ],
    },
    {
      name: 'title',
      label: 'タイトル',
      type: 'text',
      required: true,
      placeholder: '例: 夏季休業のお知らせ',
    },
    {
      name: 'body',
      label: '本文',
      type: 'textarea',
      placeholder: '詳しい内容があれば入力してください（省略可）',
    },
  ],
};

const CASES_FORM: QuickFormDescriptor = {
  blockType: 'cases',
  itemsField: 'items',
  title: '解決事例を追加',
  itemLabel: '事例',
  description: '新しい解決事例を 1 件追加します。',
  maxRows: 20,
  whenFull: 'rotate',
  insertPosition: 'start',
  titleField: 'title',
  fields: [
    {
      name: 'title',
      label: 'タイトル',
      type: 'text',
      required: true,
      placeholder: '例: 遺産分割協議を 3 か月で解決',
    },
    {
      name: 'category',
      label: '分野',
      type: 'text',
      placeholder: '例: 相続、離婚、交通事故',
    },
    { name: 'overview', label: '事案概要', type: 'textarea' },
    { name: 'result', label: '解決結果', type: 'textarea' },
    {
      name: 'clientLabel',
      label: '依頼者属性',
      type: 'text',
      placeholder: '例: 40 代男性',
    },
  ],
};

const MENU_LIST_FORM: QuickFormDescriptor = {
  blockType: 'menu_list',
  itemsField: 'items',
  grouping: { field: 'categories', nameField: 'name', itemsField: 'items', label: 'カテゴリ' },
  title: 'メニューに品目を追加',
  itemLabel: '品目',
  description:
    '既存のカテゴリを選んで料理・ドリンクを 1 件追加します。カテゴリの追加や並び替えは「詳細編集」から行えます。',
  maxRows: 40,
  whenFull: 'error',
  insertPosition: 'end',
  titleField: 'name',
  fields: [
    {
      name: 'name',
      label: '品名',
      type: 'text',
      required: true,
      placeholder: '例: 季節の野菜パスタ',
    },
    {
      name: 'description',
      label: '説明',
      type: 'textarea',
      placeholder: '食材やおすすめポイントなど（省略可）',
    },
    {
      name: 'price_jpy',
      label: '価格（円・税込）',
      type: 'number',
      min: 0,
      integer: true,
      placeholder: '例: 1200',
      hint: '数字のみ。「時価」などにしたい場合は下の価格表示に入力してください。',
    },
    {
      name: 'price_label',
      label: '価格表示（自由表記）',
      type: 'text',
      placeholder: '例: 時価 / ハーフ ¥800・フル ¥1,500',
    },
    {
      name: 'image',
      label: '写真（任意）',
      type: 'image',
      hint: '料理の写真をアップロードできます。',
    },
    { name: 'is_recommended', label: 'おすすめマークを表示する', type: 'checkbox' },
  ],
};

const SERVICE_MENU_FORM: QuickFormDescriptor = {
  blockType: 'service_menu',
  itemsField: 'items',
  grouping: { field: 'categories', nameField: 'name', itemsField: 'items', label: 'カテゴリ' },
  title: 'メニューに施術を追加',
  itemLabel: 'メニュー',
  description:
    '既存のカテゴリを選んで施術メニューを 1 件追加します。カテゴリの追加や並び替えは「詳細編集」から行えます。',
  maxRows: 40,
  whenFull: 'error',
  insertPosition: 'end',
  titleField: 'name',
  fields: [
    {
      name: 'name',
      label: 'メニュー名',
      type: 'text',
      required: true,
      placeholder: '例: カット + 炭酸スパ',
    },
    {
      name: 'description',
      label: '説明',
      type: 'textarea',
      placeholder: '施術内容やおすすめポイントなど（省略可）',
    },
    {
      name: 'duration_minutes',
      label: '所要時間（分）',
      type: 'number',
      min: 1,
      max: 720,
      integer: true,
      placeholder: '例: 60',
    },
    {
      name: 'price_jpy',
      label: '価格（円・税込）',
      type: 'number',
      min: 0,
      integer: true,
      placeholder: '例: 8000',
    },
    {
      name: 'price_label',
      label: '価格表示（自由表記）',
      type: 'text',
      placeholder: '例: 8,000円〜 / コースにより異なる',
    },
    { name: 'is_recommended', label: 'おすすめバッジを表示する', type: 'checkbox' },
  ],
};

const PRODUCT_GRID_FORM: QuickFormDescriptor = {
  blockType: 'product_grid',
  itemsField: 'items',
  title: '商品を追加',
  itemLabel: '商品',
  description: '新商品を 1 件追加します。商品一覧の先頭に表示されます。',
  maxRows: 48,
  whenFull: 'error',
  insertPosition: 'start',
  titleField: 'name',
  fields: [
    {
      name: 'name',
      label: '商品名',
      type: 'text',
      required: true,
      placeholder: '例: オーガニックコットンのストール',
    },
    {
      name: 'description',
      label: '説明',
      type: 'textarea',
      placeholder: '素材・サイズ・特長など（省略可）',
    },
    {
      name: 'price_jpy',
      label: '価格（円・税込）',
      type: 'number',
      min: 0,
      integer: true,
      placeholder: '例: 4400',
    },
    {
      name: 'price_label',
      label: '価格表示（自由表記）',
      type: 'text',
      placeholder: '例: 5枚 ¥7,700〜 / OPEN PRICE',
    },
    {
      name: 'image',
      label: '商品写真（任意）',
      type: 'image',
      hint: '商品の写真をアップロードできます。',
    },
    {
      name: 'stock_label',
      label: '在庫表記',
      type: 'text',
      placeholder: '例: 在庫あり / 残りわずか / 完売',
    },
    {
      name: 'href',
      label: '商品ページURL（任意）',
      type: 'url',
      placeholder: 'https://...',
      hint: 'BASE / Shopify / STORES などの商品詳細ページの URL。',
    },
    { name: 'is_featured', label: 'おすすめバッジを表示する', type: 'checkbox' },
  ],
};

const GALLERY_FORM: QuickFormDescriptor = {
  blockType: 'gallery',
  itemsField: 'items',
  title: 'ギャラリーに写真を追加',
  itemLabel: '写真',
  description: '新しい施術例の写真を追加します。ギャラリーの先頭に表示されます。',
  maxRows: 24,
  whenFull: 'error',
  insertPosition: 'start',
  titleField: 'caption',
  batch: { imageField: 'after', maxPerRequest: 12, disabledForLayouts: ['pairs'] },
  fields: [
    {
      name: 'before',
      label: 'Before 写真（任意）',
      type: 'image',
      layouts: ['pairs'],
      hint: '施術前の写真。仕上がり写真と並べて表示されます。',
    },
    { name: 'after', label: '仕上がり写真', type: 'image', required: true },
    {
      name: 'caption',
      label: '説明（任意）',
      type: 'text',
      placeholder: '例: ハイライトカラー / 所要 90 分',
    },
    { name: 'staff_name', label: '担当スタイリスト（任意）', type: 'text' },
    { name: 'service_name', label: '施術メニュー名（任意）', type: 'text' },
  ],
};

const QUICK_FORM_DESCRIPTORS: Record<string, QuickFormDescriptor> = {
  cases: CASES_FORM,
  gallery: GALLERY_FORM,
  lawNews: LAW_NEWS_FORM,
  menu_list: MENU_LIST_FORM,
  news: NEWS_FORM,
  product_grid: PRODUCT_GRID_FORM,
  service_menu: SERVICE_MENU_FORM,
};

export function getQuickFormDescriptor(
  blockType: string | null | undefined,
): QuickFormDescriptor | null {
  if (!blockType) return null;
  return QUICK_FORM_DESCRIPTORS[blockType] ?? null;
}

/** クイック投稿の保存値。文字列のほか、数値・真偽値・アップロード参照を含む */
export type QuickItemValue =
  | string
  | number
  | boolean
  | { relationTo: 'media'; value: string | number };

export type QuickItemValidation =
  | { ok: true; item: Record<string, QuickItemValue> }
  | { ok: false; error: string };

/**
 * フォーム入力を検証し、descriptor に定義されたフィールドだけを残した
 * 正規化済みアイテムを返す。image フィールドはクライアントからメディア ID
 * （文字列）で渡され、ここで upload フィールドの polymorphic 形式に変換する。
 */
export function validateQuickItem(
  descriptor: QuickFormDescriptor,
  input: unknown,
): QuickItemValidation {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, error: '入力内容が不正です。' };
  }
  const record = input as Record<string, unknown>;
  const item: Record<string, QuickItemValue> = {};

  for (const field of descriptor.fields) {
    const raw = record[field.name];

    if (field.type === 'checkbox') {
      if (raw === undefined || raw === null || raw === '') continue;
      const bool =
        raw === true || raw === 'true' ? true : raw === false || raw === 'false' ? false : null;
      if (bool === null) {
        return { ok: false, error: `「${field.label}」の値が不正です。` };
      }
      item[field.name] = bool;
      continue;
    }

    if (field.type === 'number') {
      const text =
        typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
      if (!text) {
        if (field.required) return { ok: false, error: `「${field.label}」を入力してください。` };
        continue;
      }
      const num = Number(text);
      if (!Number.isFinite(num)) {
        return { ok: false, error: `「${field.label}」は数字で入力してください。` };
      }
      if (field.integer && !Number.isInteger(num)) {
        return { ok: false, error: `「${field.label}」は整数で入力してください。` };
      }
      if (field.min !== undefined && num < field.min) {
        return { ok: false, error: `「${field.label}」は ${field.min} 以上で入力してください。` };
      }
      if (field.max !== undefined && num > field.max) {
        return { ok: false, error: `「${field.label}」は ${field.max} 以下で入力してください。` };
      }
      item[field.name] = num;
      continue;
    }

    if (field.type === 'image') {
      const id = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
      if (!id) {
        if (field.required) return { ok: false, error: `「${field.label}」を選択してください。` };
        continue;
      }
      if (id.length > 128) {
        return { ok: false, error: `「${field.label}」の指定が不正です。` };
      }
      // Postgres の数値 ID は数値で渡さないと relationship が解決できない
      item[field.name] = { relationTo: 'media', value: /^\d+$/.test(id) ? Number(id) : id };
      continue;
    }

    // text / textarea / date / select / url
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value) {
      if (field.required) return { ok: false, error: `「${field.label}」を入力してください。` };
      continue;
    }
    if (value.length > 5000) {
      return { ok: false, error: `「${field.label}」が長すぎます。` };
    }
    if (field.type === 'select' && field.options) {
      if (!field.options.some((option) => option.value === value)) {
        return { ok: false, error: `「${field.label}」の選択肢が不正です。` };
      }
    }
    // 配信側スキーマ（theme.ts の ProductGridItemSchema 等）と同一の許可規則。
    // protocol-relative `//evil.example` は startsWith('/') を通過するため明示的に拒否する
    if (
      field.type === 'url' &&
      !value.startsWith('https://') &&
      !(value.startsWith('/') && !value.startsWith('//'))
    ) {
      return {
        ok: false,
        error: `「${field.label}」は https:// または / で始まる URL を入力してください。`,
      };
    }

    item[field.name] = value;
  }

  return { ok: true, item };
}

export type AppendQuickItemResult =
  | { ok: true; blocks: Array<Record<string, unknown>>; trimmed: boolean }
  | { ok: false; error: string };

/**
 * blocks 配列の中から対象ブロックを探し、descriptor の挿入規則に従って
 * 1 件追加した新しい blocks 配列を返す。
 * - grouping あり: groupName で指定されたカテゴリの items に追加
 * - whenFull が rotate なら反対端の 1 件を削り、error なら追加を拒否する
 */
export function appendQuickItem(
  blocks: unknown,
  descriptor: QuickFormDescriptor,
  item: Record<string, QuickItemValue>,
  groupName?: string | null,
): AppendQuickItemResult {
  return appendQuickItems(blocks, descriptor, [item], groupName);
}

/**
 * appendQuickItem の複数件版（写真の一括追加など）。挿入順は items の
 * 並びを保ち、insertPosition に従ってまとめて先頭または末尾に入る。
 */
export function appendQuickItems(
  blocks: unknown,
  descriptor: QuickFormDescriptor,
  items: ReadonlyArray<Record<string, QuickItemValue>>,
  groupName?: string | null,
): AppendQuickItemResult {
  if (items.length === 0) {
    return { ok: false, error: '追加する内容がありません。' };
  }
  if (!Array.isArray(blocks)) {
    return { ok: false, error: 'ページにブロックがありません。' };
  }

  const index = blocks.findIndex(
    (block) =>
      !!block &&
      typeof block === 'object' &&
      (block as { blockType?: unknown }).blockType === descriptor.blockType,
  );
  if (index === -1) {
    return { ok: false, error: `このページに「${descriptor.title}」の掲載先がありません。` };
  }

  const target = blocks[index] as Record<string, unknown>;

  if (descriptor.grouping) {
    const grouping = descriptor.grouping;
    const name = typeof groupName === 'string' ? groupName.trim() : '';
    if (!name) {
      return { ok: false, error: `「${grouping.label}」を選択してください。` };
    }
    const groups = Array.isArray(target[grouping.field])
      ? (target[grouping.field] as Array<Record<string, unknown>>)
      : [];
    const groupIndex = groups.findIndex((group) => group?.[grouping.nameField] === name);
    if (groupIndex === -1) {
      return {
        ok: false,
        error: `${grouping.label}「${name}」が見つかりません。詳細編集で確認してください。`,
      };
    }

    const existing = Array.isArray(groups[groupIndex]?.[grouping.itemsField])
      ? (groups[groupIndex]![grouping.itemsField] as unknown[])
      : [];
    const inserted = insertWithLimit(existing, items, descriptor);
    if (!inserted.ok) {
      return {
        ok: false,
        error: limitError(
          `${grouping.label}「${name}」の${descriptor.itemLabel}`,
          descriptor,
          existing.length,
        ),
      };
    }

    const nextGroups = groups.map((group, i) =>
      i === groupIndex ? { ...group, [grouping.itemsField]: inserted.items } : group,
    );
    const nextBlocks = blocks.map((block, i) =>
      i === index
        ? { ...target, [grouping.field]: nextGroups }
        : (block as Record<string, unknown>),
    );
    return { ok: true, blocks: nextBlocks, trimmed: inserted.trimmed };
  }

  const existing = Array.isArray(target[descriptor.itemsField])
    ? (target[descriptor.itemsField] as unknown[])
    : [];
  const inserted = insertWithLimit(existing, items, descriptor);
  if (!inserted.ok) {
    return {
      ok: false,
      error: limitError(descriptor.itemLabel, descriptor, existing.length),
    };
  }

  const nextBlocks = blocks.map((block, i) =>
    i === index
      ? { ...target, [descriptor.itemsField]: inserted.items }
      : (block as Record<string, unknown>),
  );
  return { ok: true, blocks: nextBlocks, trimmed: inserted.trimmed };
}

function insertWithLimit(
  existing: unknown[],
  added: ReadonlyArray<Record<string, QuickItemValue>>,
  descriptor: QuickFormDescriptor,
): { ok: true; items: unknown[]; trimmed: boolean } | { ok: false } {
  let items =
    descriptor.insertPosition === 'end' ? [...existing, ...added] : [...added, ...existing];
  let trimmed = false;
  if (descriptor.maxRows && items.length > descriptor.maxRows) {
    if (descriptor.whenFull === 'error') return { ok: false };
    items =
      descriptor.insertPosition === 'end'
        ? items.slice(items.length - descriptor.maxRows)
        : items.slice(0, descriptor.maxRows);
    trimmed = true;
  }
  return { ok: true, items, trimmed };
}

/** whenFull: error の上限メッセージ。残り枠があれば件数を案内する */
function limitError(
  subject: string,
  descriptor: QuickFormDescriptor,
  existingCount: number,
): string {
  const remaining = Math.max(0, (descriptor.maxRows ?? 0) - existingCount);
  return remaining > 0
    ? `${subject}の掲載上限（${descriptor.maxRows}件）を超えます。追加できるのはあと${remaining}件です。`
    : `${subject}が掲載上限（${descriptor.maxRows}件）に達しています。詳細編集で整理してください。`;
}
